import React from 'react'
import {motion} from 'framer-motion'
import {Link} from 'react-router-dom'
import {useCourses} from '../../hooks/useCourses'
import {useEnrollments} from '../../hooks/useEnrollments'
import {useAnalytics} from '../../hooks/useAnalytics'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import SEOHead from '../../components/SEOHead'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiBook, FiUsers, FiDollarSign, FiTrendingUp, FiPlus, FiEdit, FiEye, FiSettings, FiPalette, FiHome, FiType} = FiIcons

const AdminDashboard = () => {
  const {courses, loading} = useCourses(true) // Include unpublished courses
  const {enrollments} = useEnrollments()
  const {trackCTAClick} = useAnalytics()

  const handleQuickActionClick = (action, href) => {
    trackCTAClick(`admin_quick_action_${action}`, 'admin_dashboard', href)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  const publishedCourses = courses.filter(course => course.is_published)
  const totalRevenue = enrollments.reduce((acc, enrollment) => acc + (enrollment.amount_paid || 0), 0)

  const stats = [
    {
      name: 'Total Courses',
      value: courses.length,
      published: publishedCourses.length,
      icon: FiBook,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      name: 'Total Students',
      value: new Set(enrollments.map(e => e.user_id)).size,
      icon: FiUsers,
      color: 'text-green-600 bg-green-100'
    },
    {
      name: 'Total Revenue',
      value: `$${totalRevenue.toFixed(2)}`,
      icon: FiDollarSign,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      name: 'Enrollments',
      value: enrollments.length,
      icon: FiTrendingUp,
      color: 'text-orange-600 bg-orange-100'
    }
  ]

  const quickActions = [
    {
      name: 'Create Course',
      href: '/admin/courses/new',
      icon: FiPlus,
      color: 'bg-nutrition-50 hover:bg-nutrition-100 text-nutrition-600',
      action: 'create_course'
    },
    {
      name: 'Manage Courses',
      href: '/admin/courses',
      icon: FiEdit,
      color: 'bg-blue-50 hover:bg-blue-100 text-blue-600',
      action: 'manage_courses'
    },
    {
      name: 'Site Content',
      href: '/admin/content',
      icon: FiType,
      color: 'bg-orange-50 hover:bg-orange-100 text-orange-600',
      action: 'site_content'
    },
    {
      name: 'Bulk Import',
      href: '/admin/bulk-import',
      icon: FiTrendingUp,
      color: 'bg-purple-50 hover:bg-purple-100 text-purple-600',
      action: 'bulk_import'
    },
    {
      name: 'Branding',
      href: '/admin/branding',
      icon: FiPalette,
      color: 'bg-indigo-50 hover:bg-indigo-100 text-indigo-600',
      action: 'branding'
    },
    {
      name: 'Homepage',
      href: '/admin/homepage',
      icon: FiHome,
      color: 'bg-green-50 hover:bg-green-100 text-green-600',
      action: 'homepage'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Admin Dashboard | Empower Nutrition"
        description="Manage your courses, students, and content"
        url="/admin"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your courses, students, and content</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.5, delay: index * 0.1}}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <SafeIcon icon={stat.icon} className="w-6 h-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  {stat.published !== undefined && (
                    <p className="text-xs text-gray-500">{stat.published} published</p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {quickActions.map((action, index) => (
              <motion.div
                key={action.name}
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5, delay: index * 0.1}}
              >
                <Link
                  to={action.href}
                  onClick={() => handleQuickActionClick(action.action, action.href)}
                  className={`flex items-center space-x-3 p-4 rounded-lg transition-colors ${action.color}`}
                >
                  <SafeIcon icon={action.icon} className="w-5 h-5" />
                  <span className="font-medium">{action.name}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Recent Courses */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Recent Courses</h2>
            <Link
              to="/admin/courses"
              onClick={() => trackCTAClick('view_all_courses', 'admin_dashboard', '/admin/courses')}
              className="text-nutrition-600 hover:text-nutrition-700 font-medium"
            >
              View All
            </Link>
          </div>

          {courses.length === 0 ? (
            <div className="text-center py-8">
              <SafeIcon icon={FiBook} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No courses yet</h3>
              <p className="text-gray-500 mb-4">Create your first course to get started</p>
              <Link
                to="/admin/courses/new"
                onClick={() => handleQuickActionClick('create_first_course', '/admin/courses/new')}
                className="inline-flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Create Course</span>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Course</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Price</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Students</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.slice(0, 5).map((course) => {
                    const courseEnrollments = enrollments.filter(e => e.course_id === course.id)
                    return (
                      <tr key={course.id} className="border-b border-gray-100">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            {course.cover_image_url && (
                              <img
                                src={course.cover_image_url}
                                alt={course.title}
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{course.title}</p>
                              <p className="text-sm text-gray-500">{course.slug}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium">
                            {course.price > 0 ? `$${course.price}` : 'Free'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            course.is_published 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {course.is_published ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-900">{courseEnrollments.length}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Link
                            to={`/admin/courses/${course.id}`}
                            onClick={() => trackCTAClick('edit_course', 'admin_dashboard', `/admin/courses/${course.id}`)}
                            className="text-nutrition-600 hover:text-nutrition-700 font-medium"
                          >
                            Edit
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard